import React, { useEffect, useState, useCallback } from "react";
import { Container } from "reactstrap";
import PageHeader from "../../Components/shared/PageHeader";
import { getAuditLogsAPI } from "../../api/api";
import "./AuditLogs.scss";

// ── helpers ──────────────────────────────────────────────
const formatDate = (iso) => {
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }),
    time: d.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }),
    ago: timeAgo(d),
  };
};

const timeAgo = (date) => {
  const s = Math.floor((Date.now() - date) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const parseMethod = (action = "") => {
  const [method, ...rest] = action.split(" ");
  return { method, path: rest.join(" ") };
};

const hasChanges = (changes) => changes && Object.keys(changes).length > 0;

// ── EntityBadge ───────────────────────────────────────────
const EntityBadge = ({ type }) => {
  const icons = {
    integration_sync: "⚡",
    system: "⚙️",
    user: "👤",
  };
  return (
    <span className={`entity-badge entity-${type}`}>
      {icons[type] || "📋"} {type?.replace(/_/g, " ")}
    </span>
  );
};

// ── DetailPanel ───────────────────────────────────────────
const DetailPanel = ({ log, colSpan }) => (
  <tr className="audit-detail-row">
    <td colSpan={colSpan}>
      <div className="audit-detail-panel">
        <div className="detail-item">
          <div className="detail-key">Audit ID</div>
          <div className="detail-val mono">{log.auditId}</div>
        </div>
        <div className="detail-item">
          <div className="detail-key">Request ID</div>
          <div className="detail-val mono">{log.requestId || "—"}</div>
        </div>
        <div className="detail-item">
          <div className="detail-key">IP Address</div>
          <div className="detail-val mono">{log.ip || "—"}</div>
        </div>
        <div className="detail-item">
          <div className="detail-key">User Agent</div>
          <div className="detail-val">{log.userAgent || "—"}</div>
        </div>
        <div className="detail-item">
          <div className="detail-key">Tenant</div>
          <div className="detail-val">{log.tenantId}</div>
        </div>
        <div className="detail-item">
          <div className="detail-key">User ID</div>
          <div className="detail-val mono">{log.actor?.userId || "—"}</div>
        </div>
        {hasChanges(log.changes) && (
          <div className="detail-changes-block">
            <div className="detail-key" style={{ marginBottom: 8 }}>
              Changes / Payload
            </div>
            <pre>{JSON.stringify(log.changes, null, 2)}</pre>
          </div>
        )}
      </div>
    </td>
  </tr>
);

// ── AuditLogs ─────────────────────────────────────────────
const PAGE_SIZE = 10;

function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // filters
  const [search, setSearch] = useState("");
  const [entityFilter, setEntityFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");

  // pagination
  const [page, setPage] = useState(1);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getAuditLogsAPI();
      setLogs(res.data || []);
    } catch {
      setError("Failed to load audit logs.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // derived
  const filtered = logs.filter((l) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      l.auditId?.toLowerCase().includes(q) ||
      l.actor?.email?.toLowerCase().includes(q) ||
      l.action?.toLowerCase().includes(q) ||
      l.entityId?.toLowerCase().includes(q);
    const matchEntity = !entityFilter || l.entityType === entityFilter;
    const matchSource = !sourceFilter || l.source === sourceFilter;
    return matchSearch && matchEntity && matchSource;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const entityTypes = [
    ...new Set(logs.map((l) => l.entityType).filter(Boolean)),
  ];
  const sources = [...new Set(logs.map((l) => l.source).filter(Boolean))];

  const resetFilters = () => {
    setSearch("");
    setEntityFilter("");
    setSourceFilter("");
    setPage(1);
  };

  const activeFilters = [search, entityFilter, sourceFilter].filter(
    Boolean,
  ).length;

  const toggleExpand = (id) =>
    setExpandedId((prev) => (prev === id ? null : id));

  const COL_COUNT = 7;

  return (
    <div className="audit-logs-page">
      <PageHeader
        title="Audit Logs"
        breadcrumbs={[
          { label: "Dashboard", path: "/" },
          { label: "Audit Logs" },
        ]}
        secondaryActions={[{ label: "↻ Refresh", onClick: fetchLogs }]}
      />

      {/* Filters */}
      <div className="audit-filters-bar">
        <span className="filter-label">Filter:</span>

        <input
          className="filter-input"
          placeholder="Search by ID, email, action…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />

        <select
          className="filter-select"
          value={entityFilter}
          onChange={(e) => {
            setEntityFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All Entity Types</option>
          {entityTypes.map((t) => (
            <option key={t} value={t}>
              {t.replace(/_/g, " ")}
            </option>
          ))}
        </select>

        <select
          className="filter-select"
          value={sourceFilter}
          onChange={(e) => {
            setSourceFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All Sources</option>
          {sources.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        {activeFilters > 0 && (
          <button className="btn-filter-reset" onClick={resetFilters}>
            Clear filters
          </button>
        )}

        <span className="filter-count-badge">
          {filtered.length} record{filtered.length !== 1 ? "s" : ""}
        </span>
      </div>

      <Container fluid>
        <div className="audit-card">
          <table className="audit-table">
            <thead>
              <tr>
                <th>Actor</th>
                <th>Entity Type</th>
                <th>Entity</th>
                <th>Action</th>
                <th>Source</th>
                <th>Changes</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr className="audit-loading-row">
                  <td colSpan={COL_COUNT}>
                    <div className="d-flex align-items-center justify-content-center gap-2">
                      <div
                        className="spinner-border spinner-border-sm spinner-purple"
                        role="status"
                        style={{ width: 18, height: 18 }}
                      />
                      Loading audit logs…
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr className="audit-loading-row">
                  <td colSpan={COL_COUNT} style={{ color: "#e05252" }}>
                    {error}
                  </td>
                </tr>
              ) : paginated.length === 0 ? (
                <tr>
                  <td colSpan={COL_COUNT}>
                    <div className="audit-empty-state">
                      <div className="empty-icon">📋</div>
                      <div className="empty-title">No audit logs found</div>
                      <div className="empty-sub">
                        Try adjusting your filters
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                paginated.flatMap((log) => {
                  const { method, path } = parseMethod(log.action);
                  const ts = formatDate(log.createdAt);
                  const isExpanded = expandedId === log._id;

                  return [
                    <tr key={log._id} onClick={() => toggleExpand(log._id)}>
                      {/* Actor */}
                      <td>
                        <div className="audit-actor">
                          <span className="actor-email">
                            {log.actor?.email || "—"}
                          </span>
                          {log.actor?.roles?.map((r) => (
                            <span key={r} className="actor-role">
                              ● {r}
                            </span>
                          ))}
                        </div>
                      </td>

                      {/* Entity Type */}
                      <td>
                        <EntityBadge type={log.entityType} />
                      </td>

                      {/* Entity ID */}
                      <td>
                        <span className="entity-id-pill">
                          <span className="entity-dot" />
                          {log.entityId}
                        </span>
                      </td>

                      {/* Action */}
                      <td className="audit-action-col">
                        <span className="method-tag">{method}</span>
                        {path}
                      </td>

                      {/* Source */}
                      <td>
                        <span className="source-badge">{log.source}</span>
                      </td>

                      {/* Changes */}
                      <td>
                        <div className="changes-indicator">
                          <span
                            className={`change-dot ${hasChanges(log.changes) ? "" : "no-change"}`}
                          />
                          <span className="change-text">
                            {hasChanges(log.changes) ? "Has data" : "None"}
                          </span>
                        </div>
                      </td>

                      {/* Timestamp */}
                      <td>
                        <div className="audit-time">
                          <span className="time-date">
                            {ts.date} {ts.time}
                          </span>
                          <span className="time-ago">{ts.ago}</span>
                        </div>
                      </td>
                    </tr>,

                    isExpanded && (
                      <DetailPanel
                        key={`${log._id}-detail`}
                        log={log}
                        colSpan={COL_COUNT}
                      />
                    ),
                  ];
                })
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {!loading && filtered.length > PAGE_SIZE && (
            <div className="audit-pagination">
              <span className="pagination-info">
                Showing{" "}
                <strong>
                  {(page - 1) * PAGE_SIZE + 1}–
                  {Math.min(page * PAGE_SIZE, filtered.length)}
                </strong>{" "}
                of <strong>{filtered.length}</strong>
              </span>
              <div className="pagination-controls">
                <button
                  className="page-btn"
                  onClick={() => setPage((p) => p - 1)}
                  disabled={page === 1}
                >
                  ‹
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(
                    (p) =>
                      p === 1 || p === totalPages || Math.abs(p - page) <= 1,
                  )
                  .reduce((acc, p, i, arr) => {
                    if (i > 0 && p - arr[i - 1] > 1) acc.push("…");
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) =>
                    p === "…" ? (
                      <span
                        key={`ellipsis-${i}`}
                        className="page-btn"
                        style={{ cursor: "default", opacity: 0.4 }}
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={p}
                        className={`page-btn${page === p ? " active" : ""}`}
                        onClick={() => setPage(p)}
                      >
                        {p}
                      </button>
                    ),
                  )}

                <button
                  className="page-btn"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page === totalPages}
                >
                  ›
                </button>
              </div>
            </div>
          )}
        </div>
      </Container>
    </div>
  );
}

export default AuditLogs;
